import { useState, type FC } from 'react';
import { Button, Form, InputNumber, Modal, Table, Input } from 'antd';
import { Space, Select, DatePicker, Title } from '@g2u/ui-kit';
import { putDiscount } from 'queries/job/putDiscount';
import { QueryObserverResult, RefetchOptions, RefetchQueryFilters, useMutation } from '@tanstack/react-query';
import { Toast, parseErrors } from 'helpers';
import { isBefore, parse, startOfDay, format } from 'date-fns';
import styles from './customer-discount-list.module.scss';

type Discount = {
  customer_discount_id: number;
  frequency: string;
  reason: string;
  mode: string;
  discount_type: string;
  amount: string;
  active: number;
  expiry_date?: string;
  discount_desc?: string;
  approved_by_info?: {
    name: string;
  };
};
type Props = {
  discounts?: Discount[];
  refetch?: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined,
  ) => Promise<QueryObserverResult<any, unknown>>;
};

type FilterOption = 'all' | 'active' | 'inactive';

export const CustomerDiscountList: FC<Props> = ({ refetch, discounts }) => {
  const [filter, setFilter] = useState<FilterOption>('active');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentDiscount, setCurrentDiscount] = useState<Discount | null>(null);
  const [form] = Form.useForm();

  const datesBeforeNow = (current: Date | null) => {
    return !!current && isBefore(startOfDay(current), startOfDay(new Date()));
  };

  const handleEdit = (discount: Discount) => {
    setCurrentDiscount(discount);
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setCurrentDiscount(null);
  };

  const updateDiscountMutation = useMutation(putDiscount, {
    onSuccess: () => {
      refetch?.();
      setIsModalOpen(false);
      setCurrentDiscount(null);
    },
    onError: (error) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const handleSave = async () => {
    if (currentDiscount) {
      const expiryDate = form.getFieldValue('expiry_date');

      updateDiscountMutation.mutate({
        discountId: currentDiscount.customer_discount_id,
        expiryDate: format(new Date(expiryDate), 'yyyy-MM-dd'),
        active: form.getFieldValue('active'),
      });
    }
  };

  const columnsDefinition = [
    {
      key: 'frequency',
      title: 'Frequency',
      dataIndex: 'frequency',
    },
    {
      key: 'reason',
      title: 'Reason',
      dataIndex: 'reason',
    },
    {
      key: 'mode',
      title: 'Mode',
      dataIndex: 'mode',
    },
    {
      key: 'type',
      title: 'Type',
      dataIndex: 'discount_type',
    },
    {
      key: 'amount',
      title: 'Amount',
      dataIndex: 'amount',
    },
    {
      key: 'expiryDate',
      title: 'Expiry Date',
      dataIndex: 'expiry_date',
      //discounts that don't expire show up from the backend as '0000-00-00'
      render: (expiry_date: string) => (expiry_date === '0000-00-00' ? 'N/A' : expiry_date),
    },
    {
      key: 'active',
      title: 'Active',
      dataIndex: 'active',
      render: (active: number) => (active === 1 ? 'Yes' : 'No'),
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_: any, record: Discount) => (
        <Button onClick={() => handleEdit(record)} disabled={!record.active}>
          Edit
        </Button>
      ),
    },
  ];

  const filteredDiscounts = discounts?.filter((discount) => {
    if (filter === 'active') return discount.active === 1;
    if (filter === 'inactive') return discount.active === 0;
    return true;
  });

  return (
    <div className={styles.customerDiscountList}>
      <Space.$ direction={Space.Direction.Horizontal} align={Space.Align.Center} justify={Space.Justify.SpaceBetween}>
        <Title.$ level={5} className={styles.customerDiscountList__title}>
          Discounts
        </Title.$>
        <Select.$
          name="filter-by-discount-status"
          label="Filter by"
          initialValue="active"
          onChange={(value) => {
            setFilter(value);
          }}
          options={[
            { value: 'all', label: <span>All</span> },
            { value: 'active', label: <span>Active</span> },
            { value: 'inactive', label: <span>Inactive</span> },
          ]}
        />
      </Space.$>
      <br />
      <Table
        pagination={{ pageSize: 5 }}
        columns={columnsDefinition}
        dataSource={filteredDiscounts}
        rowKey="customer_discount_id"
        style={{ marginBottom: !filteredDiscounts?.length ? 16 : 0 }}
      />
      <Form form={form} onFinish={handleSave} preserve={false} labelWrap labelCol={{ sm: { span: 5, offset: 3 } }}>
        <Modal
          title="Edit Discount"
          open={isModalOpen}
          onCancel={handleCancel}
          onOk={() => {
            form.submit();
          }}
        >
          <Select.$
            name="frequency"
            label="Frequency"
            disabled
            placeholder={currentDiscount?.frequency}
            options={[
              {
                value: currentDiscount?.frequency,
              },
            ]}
          />
          <Select.$
            name="reason"
            label="Reason"
            disabled
            placeholder={currentDiscount?.reason}
            options={[
              {
                value: currentDiscount?.reason,
              },
            ]}
          />
          <Select.$
            name="discount_desc"
            label="Discount Description"
            disabled
            placeholder={currentDiscount?.discount_desc}
            options={[
              {
                value: currentDiscount?.discount_desc,
              },
            ]}
          />
          <Select.$
            name="mode"
            label="Mode"
            disabled
            placeholder={currentDiscount?.mode}
            options={[
              {
                value: currentDiscount?.mode,
              },
            ]}
          />
          <Select.$
            name="discount_type"
            label="Discount Type"
            disabled
            placeholder={currentDiscount?.discount_type}
            options={[
              {
                value: currentDiscount?.discount_type,
              },
            ]}
          />
          <Select.$
            name="active"
            label="Active"
            placeholder={currentDiscount?.active === 1 ? 'Yes' : 'No'}
            options={[
              {
                label: 'Yes',
                value: 1,
              },
              {
                label: 'No',
                value: 0,
              },
            ]}
          />
          <DatePicker.$
            name="expiry_date"
            label="Expiry Date"
            initialValue={
              currentDiscount?.expiry_date
                ? (parse(currentDiscount.expiry_date, 'yyyy-MM-dd', new Date()) as any)
                : new Date()
            }
            format="YYYY-MM-DD"
            disabledDate={datesBeforeNow}
          />
          <Form.Item label="Approved By" labelCol={{ sm: { span: 5, offset: 3 } }}>
            <Input name="approved_by_info.name" disabled placeholder={currentDiscount?.approved_by_info?.name} />
          </Form.Item>

          <Form.Item label="Amount">
            <InputNumber name="amount" disabled placeholder={currentDiscount?.amount} />
          </Form.Item>
        </Modal>
      </Form>
    </div>
  );
};
